import { computed } from "vue";
import type { Ref } from "vue";
import { useSelectorStore } from "@/_store/selector.module";
import { storeToRefs } from "pinia";
import intersectionWith from "lodash/intersectionWith";
import differenceWith from "lodash/differenceWith";
import isEqual from "lodash/isEqual";
import { SelectorValue } from "@/constants/selector";

/**
 * Composable which handles the behavior of the selectable table.
 */
export function useSelectableTable<T>(props: { items: Ref<T[]>; totalItems: Ref<number> }) {
  const selectionStore = useSelectorStore();
  const { selection } = storeToRefs(selectionStore);

  /**
   * Determines if the "Select All" checkbox should be fully checked [x].
   * True if:
   * 1. We are in "All Pages" mode (and haven't excluded anything), OR
   * 2. We are in "Normal" mode, but we manually selected every single item.
   */
  const allItemsSelected = computed({
    get() {
      // Case 1: All Pages Mode
      if (selection.value.isAllPages) {
        return selection.value.exclude.length === 0;
      }

      // Case 2: Normal Mode (Manual Selection)
      // Check if we have selected quantity equal to the total available items
      return (
        props.totalItems.value > 0 && selection.value.include.length === props.totalItems.value
      );
    },
    set(value: boolean) {
      // When the user clicks the checkbox directly
      const payload = {
        selectorValue: value ? SelectorValue.SELECT_ALL_PAGES : SelectorValue.DESELECT_ALL_PAGES,
        items: props.items.value,
      };
      onSelectionChanged(payload);
    },
  });

  /**
   * Determines if the checkbox should show a dash [-].
   * True if:
   * 1. Not fully selected AND
   * 2. Not fully empty.
   */
  const isIndeterminate = computed(() => {
    // If it's fully selected (checked), it's NOT indeterminate
    if (allItemsSelected.value) return false;

    if (selection.value.isAllPages) {
      // In "All Pages" mode, if we have exclusions, it's indeterminate
      // (because it's "All except X")
      return selection.value.exclude.length > 0;
    }

    // In "Normal" mode, it's indeterminate if we have > 0 but < Total
    return (
      selection.value.include.length > 0 && selection.value.include.length < props.totalItems.value
    );
  });

  // --- Data Helpers ---

  const selectedItems = computed(() => {
    if (selection.value.isAllPages) {
      return differenceWith(props.items.value, selection.value.exclude, isEqual);
    }
    return intersectionWith(props.items.value, selection.value.include, isEqual);
  });

  const allItemsOnPageSelected = computed(() => {
    if (props.items.value.length === 0) {
      return false;
    }

    if (selection.value.isAllPages) {
      const excludedOnPage = intersectionWith(props.items.value, selection.value.exclude, isEqual);
      return excludedOnPage.length === 0;
    }

    const selectedOnPage = intersectionWith(props.items.value, selection.value.include, isEqual);
    return selectedOnPage.length === props.items.value.length;
  });

  const selectedItemsQuantity = computed(() => {
    if (selection.value.isAllPages) {
      // Total minus the exceptions
      const count = props.totalItems.value - selection.value.exclude.length;
      return count > 0 ? count : 0;
    }
    return selection.value.include.length;
  });

  const handleAllRowsSelection = () => {
    // Toggle based on the current COMPUTED state, not a local ref
    const isCurrentlySelected = allItemsSelected.value;

    allItemsSelected.value = !isCurrentlySelected; // Triggers the setter above
  };

  const handleOneRowSelection = (selected: boolean, item: T) => {
    onSelectionChanged({
      selectorValue: selected ? SelectorValue.SELECT_ITEMS : SelectorValue.DESELECT_ITEMS,
      items: [item],
    });
  };

  const onSelectionChanged = (selectionData: { selectorValue: string; items: Array<T> }) => {
    const payload = {
      items: selectionData.items,
      totalItems: props.totalItems.value,
    };
    switch (selectionData.selectorValue) {
      case SelectorValue.DESELECT_ITEMS:
        selectionStore.deselectItems(payload);
        break;
      case SelectorValue.DESELECT_ALL_PAGES:
        selectionStore.deselectAllPages();
        break;
      case SelectorValue.SELECT_ITEMS:
        selectionStore.selectItems(payload);
        break;
      case SelectorValue.SELECT_ALL_PAGES:
        selectionStore.selectAllPages();
        break;
    }
  };

  return {
    selectedItems,
    allItemsSelected,
    allItemsOnPageSelected,
    isIndeterminate,
    handleOneRowSelection,
    handleAllRowsSelection,
    onSelectionChanged,
    selectedItemsQuantity,
  };
}
